import { FormulaDecimal } from './decimal.js';
import { evaluate } from './interpreter.js';
import * as runtime from './runtime.js';
import { isDecimal, toValue } from './values.js';
const ZERO = new FormulaDecimal(0);
const NO_TOTAL = { value: null };
function isAdditive(valueType) {
    return valueType === 'Quantity' || valueType === 'Amount';
}
/** The id beside `rateId` in a product of two references; null for any other formula. */
function otherFactor(root, rateId) {
    if (root.kind !== 'binary' || root.op !== '*' || root.left.kind !== 'ref' || root.right.kind !== 'ref')
        return null;
    if (root.left.symbol === rateId)
        return root.right.symbol;
    return root.right.symbol === rateId ? root.left.symbol : null;
}
/** A named weight when it is a member that sums; a weight that does not sum weights nothing, it is never replaced. */
function namedWeight(id, members) {
    const weight = members.get(id);
    return weight && isAdditive(weight.valueType) ? id : null;
}
/**
 * The member an entered rate or ratio is weighted by when it names none (§9.3): the factor beside it in the products that sum — a
 * Quantity or Amount member whose formula is a product of two references, one of them the rate. `VALUE = VOLUME *
 * PRICE` weights PRICE by VOLUME; `PAYROLL_TAX = PERSONNEL_COST * EMPLOYER_FEE` weights EMPLOYER_FEE by
 * PERSONNEL_COST. Null when no such product names it, when two name different factors, or when the factor does
 * not sum.
 */
function weightOf(rateId, members) {
    const factors = new Set();
    for (const m of members.values()) {
        if (!m.formula || !isAdditive(m.valueType))
            continue;
        const other = otherFactor(m.formula, rateId);
        if (other !== null)
            factors.add(other);
    }
    if (factors.size !== 1)
        return null;
    const [id] = factors;
    const weight = members.get(id);
    return weight && isAdditive(weight.valueType) ? id : null;
}
/** A total outside System.Decimal (§4.4) leaves the member without one; anything else is a defect and propagates. */
function withinRange(compute, outside) {
    try {
        return compute();
    }
    catch (e) {
        if (e instanceof RangeError)
            return outside;
        throw e;
    }
}
/** An additive member's sums, one per unit. */
class SumState {
    constructor() {
        this.buckets = new Map();
        this.overflow = false;
    }
    add(unit, value) {
        const bucket = this.buckets.get(unit);
        if (!bucket) {
            this.buckets.set(unit, { unit, sum: value });
            return;
        }
        this.overflow || (this.overflow = withinRange(() => {
            bucket.sum = runtime.add(bucket.sum, value);
            return false;
        }, true));
    }
    merge(other) {
        this.overflow || (this.overflow = other.overflow);
        for (const bucket of other.buckets.values())
            this.add(bucket.unit, bucket.sum);
    }
}
/** A weighted member's Σ(value × weight) and Σ weight, and the least and greatest of its values. */
class WeightedState {
    constructor() {
        this.numerator = ZERO;
        this.weight = ZERO;
        this.weightUnits = new Set();
        this.least = null;
        this.greatest = null;
        this.overflow = false;
    }
    addValue(value) {
        if (this.least === null || value.lessThan(this.least))
            this.least = value;
        if (this.greatest === null || value.greaterThan(this.greatest))
            this.greatest = value;
    }
    addPair(value, weight, unit) {
        this.weightUnits.add(unit);
        this.addSums(runtime.mul(value, weight), weight);
    }
    merge(other) {
        this.overflow || (this.overflow = other.overflow);
        for (const unit of other.weightUnits)
            this.weightUnits.add(unit);
        this.addSums(other.numerator, other.weight);
        if (other.least !== null)
            this.addValue(other.least);
        if (other.greatest !== null)
            this.addValue(other.greatest);
    }
    addSums(numerator, weight) {
        this.overflow || (this.overflow = withinRange(() => {
            this.numerator = runtime.add(this.numerator, numerator);
            this.weight = runtime.add(this.weight, weight);
            return false;
        }, true));
    }
}
export class CellAggregation {
    constructor(members, options = { rounding: 'halfEven' }) {
        this.options = options;
        this.members = new Map();
        this.weights = new Map();
        this.sums = new Map();
        this.weighted = new Map();
        this.totals = new Map();
        for (const member of members) {
            this.members.set(member.id, member);
            if (isAdditive(member.valueType))
                this.sums.set(member.id, new SumState());
        }
        for (const member of this.members.values()) {
            if (isAdditive(member.valueType) || member.formula)
                continue;
            const weight = member.weightedBy ? namedWeight(member.weightedBy, this.members) : weightOf(member.id, this.members);
            if (weight === null)
                continue;
            this.weights.set(member.id, weight);
            this.weighted.set(member.id, new WeightedState());
        }
    }
    /** Adds one cell's values. */
    add(cell) {
        this.totals.clear();
        for (const [id, state] of this.sums) {
            const value = toValue(cell.value(id));
            if (isDecimal(value))
                state.add(this.unitOf(id, cell), value);
        }
        for (const [id, state] of this.weighted)
            this.addWeighted(id, state, cell);
        return this;
    }
    /** Adds another aggregation of the same members: the result is the aggregation of both's cells. */
    merge(other) {
        if (other.members.size !== this.members.size || [...other.members.keys()].some((id) => !this.members.has(id))) {
            throw new Error('Only aggregations of the same members merge.');
        }
        this.totals.clear();
        for (const [id, state] of this.sums)
            state.merge(other.sums.get(id));
        for (const [id, state] of this.weighted)
            state.merge(other.weighted.get(id));
        return this;
    }
    /** The member's total (§9.3); a member that is not one of these has none. */
    total(id) {
        const known = this.totals.get(id);
        if (known)
            return known;
        const member = this.members.get(id);
        if (!member)
            return NO_TOTAL;
        this.totals.set(id, NO_TOTAL); // a member read while its own total is computed — a cycle — has none
        const total = this.compute(member);
        this.totals.set(id, total);
        return total;
    }
    compute(member) {
        if (isAdditive(member.valueType))
            return this.sumTotal(member);
        if (member.formula)
            return { value: this.evaluated(member.formula) };
        return { value: this.weightedTotal(member) };
    }
    sumTotal(member) {
        const state = this.sums.get(member.id);
        const buckets = [...state.buckets.values()];
        if (member.valueType !== 'Quantity')
            return { value: state.overflow ? null : (buckets[0]?.sum ?? ZERO) };
        if (state.overflow)
            return { value: null, buckets: [] };
        const value = buckets.length === 0 ? ZERO : buckets.length === 1 ? buckets[0].sum : null;
        return { value, buckets };
    }
    weightedTotal(member) {
        const state = this.weighted.get(member.id);
        if (!state || state.overflow || state.weightUnits.size > 1)
            return null;
        if (state.weight.isZero())
            return state.least !== null && state.greatest !== null && state.least.equals(state.greatest) ? state.least : null;
        return withinRange(() => runtime.div(state.numerator, state.weight), null);
    }
    /** The formula over the members' totals; null where it has no number. */
    evaluated(formula) {
        return withinRange(() => {
            const value = evaluate(formula, (id) => this.total(id).value, this.options);
            return isDecimal(value) ? value : null;
        }, null);
    }
    addWeighted(id, state, cell) {
        const value = toValue(cell.value(id));
        if (!isDecimal(value))
            return;
        state.addValue(value);
        const weightId = this.weights.get(id);
        const weight = toValue(cell.value(weightId));
        if (!isDecimal(weight))
            return;
        state.overflow || (state.overflow = withinRange(() => {
            state.addPair(value, weight, this.unitOf(weightId, cell));
            return false;
        }, true));
    }
    unitOf(id, cell) {
        return this.members.get(id).valueType === 'Quantity' ? (cell.unit?.(id) ?? null) : null;
    }
}
